import * as React from 'react';
import {
  AutosaveField,
  CitationGrid,
  PanelSection,
  Quote,
  SegmentedControl,
  SidePanel,
  WorkspaceButton,
  type CitationGridItem,
} from '@/components/ui/workspace';
import { ModuleSelect, type ModuleOption } from '@/components/requirement-import/module-select';
import { SourceCite } from '@/components/ui/citation-chip';
import { StatusChip } from '@/components/ui/status-chip';
import { PanelTextEditor } from '@/components/requirement-library/library-panel';
import type {
  Criticality,
  EngagementRequirement,
  RequirementEvidence,
} from '@/lib/api/engagement-requirements';
import { RequirementActivity } from './requirement-activity';
import {
  CHOOSABLE_CRITICALITIES,
  CRITICALITY_LABEL,
  NOT_MAPPED_LABEL,
  NOT_MAPPED_VIEW,
  addedFromDiscovery,
  asCriticality,
  citationOf,
  evidenceByline,
  evidenceLine,
  evidenceWithdrawnText,
  formatDate,
  librarySourceLabel,
  moduleLabelOf,
} from './requirements-logic';

/**
 * Side panel for one engagement requirement (T-0142; E11-F04 scenarios 2,
 * 9 and 10).
 *
 * Reference: `reqPanel()` and `citation()` in
 * `docs/requirement-library/prototype/selection.js`. Shows the editable
 * text, the criticality as a segmented control, and the Citation block:
 * where the requirement came from and why it is here (amendment 01,
 * Decision 34). The citation is a record and is never editable; neither
 * is the number (Decision 32).
 *
 * A requirement added from discovery says so as its source, and the
 * Discovery evidence section lists each item it cites (E12-F10 §3): the
 * quote, its number, who said it, where and when.
 *
 * The Activity section (T-0152) lists the requirement's history from the
 * audit trail: see `requirement-activity.tsx`.
 *
 * Caliber enriches the client's RTM, never rewrites it (ADR-025, 24 October
 * 2026): a requirement matched to the library keeps the client's words, and
 * the library item's wording is offered as Suggested wording, the two side
 * by side, with "Use library wording" (an audited text edit). The client
 * area is the client's own grouping and can be changed. A requirement with
 * no library item can be mapped to a module, or left Not mapped; a library
 * requirement stays in its item's module.
 */

const SEGMENTS = CHOOSABLE_CRITICALITIES.map((c) => ({
  value: c,
  label: c === 'not_applicable' ? 'N/A' : CRITICALITY_LABEL[c],
}));

const DIFF_BOX =
  'mt-2 rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-accent-amber-50 px-3 py-2.5 text-body-sm text-ink-700';
const LABEL = 'text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500';

const mono = (text: string) => <span className="font-mono text-meta">{text}</span>;

const MATCH_VERDICT_LABEL: Record<string, string> = {
  same: 'Same',
  broader: 'Broader',
  none: 'No match',
};

/** The rows of the Citation block for one requirement. */
export function citationItems(
  row: EngagementRequirement,
  opts: {
    /** Display name for a user id, when the screen knows it. */
    userName?: (userId: string) => string | null;
    /** File name for a supporting document id, when the screen knows it. */
    documentName?: (documentId: string) => string | null;
  } = {},
): CitationGridItem[] {
  const c = citationOf(row);
  const items: CitationGridItem[] = [];
  let addedBy = opts.userName?.(row.created_by) ?? null;
  let addedAt = row.created_at;

  if (addedFromDiscovery(row)) {
    // E12-F10 §3: added from a discovery suggestion. A library match keeps
    // its library citation; the evidence behind it is listed below the grid.
    items.push({ label: 'Source', value: 'From discovery' });
    if (c.kind === 'library') {
      items.push({
        label: 'Library match',
        value: (
          <>
            {mono(c.libraryNumber)}
            {c.moduleVersion != null ? `, version ${c.moduleVersion}` : ''}
          </>
        ),
      });
      items.push({ label: 'Library source', value: librarySourceLabel(c) });
    } else {
      items.push({ label: 'Library match', value: 'None: a client-specific requirement' });
    }
    if (!row.discovery_evidence?.length && c.kind === 'discovery') {
      // A server from before the evidence was sent with the row: the numbers only.
      items.push({ label: 'Evidence', value: c.evidenceNumbers.join(', '), mono: true });
    }
    if (row.load) {
      addedBy = row.load.created_by_name ?? opts.userName?.(row.load.created_by) ?? null;
      addedAt = row.load.created_at;
    }
  } else if (c.kind === 'library') {
    items.push({ label: 'Source', value: 'Firm requirement library' });
    items.push({
      label: 'Item',
      value: (
        <>
          {mono(c.libraryNumber)} · {row.module_name}
          {c.moduleVersion != null ? ` version ${c.moduleVersion}` : ''}
        </>
      ),
    });
    items.push({ label: 'Library source', value: librarySourceLabel(c) });
    if (c.matchedFrom) {
      // T-0263: matched from a row of the client's spreadsheet.
      const m = c.matchedFrom;
      items.push({ label: 'Matched from', value: m.file, mono: true });
      items.push({
        label: 'Location',
        value:
          [m.sheet ? `Sheet "${m.sheet}"` : null, m.row != null ? `row ${m.row}` : null]
            .filter(Boolean)
            .join(', ') || 'Not recorded',
      });
      items.push({
        label: 'Client reference',
        value: m.clientRef ?? 'None',
        mono: Boolean(m.clientRef),
      });
      if (c.matchVerdict) {
        items.push({
          label: 'Match',
          value: `${MATCH_VERDICT_LABEL[c.matchVerdict] ?? c.matchVerdict}${
            c.matchScore != null ? `, meaning score ${c.matchScore.toFixed(2)}` : ''
          }`,
        });
      }
    }
    items.push({
      label: 'Why added',
      value: row.load?.reason ? (
        row.load.reason
      ) : (
        <span className="text-ink-300">No reason recorded for this load</span>
      ),
    });
    if (row.load) {
      addedBy = row.load.created_by_name ?? opts.userName?.(row.load.created_by) ?? null;
      addedAt = row.load.created_at;
    }
  } else if (c.kind === 'spreadsheet') {
    items.push({ label: 'Source', value: 'Client spreadsheet' });
    items.push({ label: 'File', value: c.fileName || 'Not recorded', mono: Boolean(c.fileName) });
    items.push({
      label: 'Location',
      value:
        [c.sheet ? `Sheet "${c.sheet}"` : null, c.row != null ? `row ${c.row}` : null]
          .filter(Boolean)
          .join(', ') || 'Not recorded',
    });
    items.push({
      label: 'Client reference',
      value: c.clientRef ?? 'None',
      mono: Boolean(c.clientRef),
    });
    if (row.load) {
      addedBy = row.load.created_by_name ?? addedBy;
      addedAt = row.load.created_at;
    }
  } else if (c.kind === 'manual') {
    items.push({ label: 'Source', value: 'Written by a consultant' });
    items.push({ label: 'Why added', value: c.reason });
    if (c.supportingDocumentId) {
      const name = opts.documentName?.(c.supportingDocumentId);
      items.push({
        label: 'Supporting document',
        value: name ?? 'A document in this engagement',
        mono: Boolean(name),
      });
    }
  } else {
    items.push({ label: 'Source', value: 'Not recorded' });
  }

  const date = formatDate(addedAt);
  items.push({ label: 'Added', value: addedBy ? `${date} by ${addedBy}` : date });
  return items;
}

/**
 * The discovery evidence a requirement cites, each item as the suggestion
 * card shows it: the quote, then its number, who said it, where (the
 * citation pill) and when. A withdrawn item stays, struck through, with
 * when and why (Decision 54.20).
 */
export function DiscoveryEvidenceList({ evidence }: { evidence: RequirementEvidence[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {evidence.map((e) => {
        const l = evidenceLine(e);
        const parts: React.ReactNode[] = [];
        if (l.number) {
          parts.push(
            <span key="n" className="font-mono font-medium text-ink-700">
              {l.number}
            </span>,
          );
        }
        if (l.who) {
          parts.push(
            <span key="w" className="text-ink-700">
              {l.who}
            </span>,
          );
        }
        if (l.where) {
          parts.push(
            <SourceCite
              key="c"
              kind={l.where.kind}
              label={l.where.label}
              location={l.where.location}
              withdrawn={l.withdrawn}
            />,
          );
        }
        if (l.date) parts.push(<span key="d">{l.date}</span>);
        return (
          <li
            key={e.evidence_id}
            aria-label={evidenceByline(e)}
            className="rounded-chip border border-ink-100 bg-paper-50 px-2.5 py-2"
          >
            {e.text ? (
              <Quote size="sm" struck={l.withdrawn}>
                {e.text}
              </Quote>
            ) : null}
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-meta text-ink-500">
              {parts.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? <span aria-hidden="true">·</span> : null}
                  {p}
                </React.Fragment>
              ))}
              {l.withdrawn ? (
                <StatusChip tone="frozen" size="sm">
                  Withdrawn
                </StatusChip>
              ) : null}
            </div>
            {l.withdrawn ? (
              <p className="mt-1 text-meta text-ink-500">{evidenceWithdrawnText(e)}</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The library item's wording beside the requirement's own, with the action
 * that takes it. For a row matched from the client's spreadsheet the left
 * side is the client's wording, which stays unless someone chooses.
 */
export function SuggestedWording({
  current,
  library,
  libraryNumber,
  fromClient,
  disabled,
  onUse,
}: {
  current: string;
  library: string;
  libraryNumber: string | null;
  fromClient: boolean;
  disabled: boolean;
  /** Null when the requirement cannot be edited (removed). */
  onUse: (() => void) | null;
}) {
  return (
    <div className={DIFF_BOX} role="group" aria-label="Suggested wording">
      <div className="mb-1 font-semibold text-ink-900">Suggested wording</div>
      <p className="mb-2 text-meta text-ink-500">
        {fromClient
          ? `The library item${libraryNumber ? ` ${libraryNumber}` : ''} this row was matched to words it differently. The client’s wording stays unless you use the library’s.`
          : 'The library item words it differently.'}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <p className={`${LABEL} mb-1`}>{fromClient ? 'Client wording' : 'This requirement'}</p>
          <p className="whitespace-pre-wrap break-words text-ink-900">{current}</p>
        </div>
        <div className="min-w-0">
          <p className={`${LABEL} mb-1`}>Library wording</p>
          <p className="whitespace-pre-wrap break-words text-ink-900">{library}</p>
        </div>
      </div>
      {onUse ? (
        <div className="mt-2">
          <WorkspaceButton disabled={disabled} onClick={onUse}>
            Use library wording
          </WorkspaceButton>
        </div>
      ) : null}
    </div>
  );
}

export interface RequirementPanelProps {
  engagementId: string;
  row: EngagementRequirement;
  /** Library wording this row differs from, or null. */
  libraryWording: string | null;
  /** Active library modules, to map a requirement with no library item. */
  modules?: ReadonlyArray<ModuleOption>;
  busy: boolean;
  userName?: (userId: string) => string | null;
  documentName?: (documentId: string) => string | null;
  onClose: () => void;
  onCommitText: (row: EngagementRequirement, text: string) => void;
  onSetCriticality: (row: EngagementRequirement, value: Criticality) => void;
  /** Save the client area; null clears it. */
  onSetClientArea?: (row: EngagementRequirement, value: string | null) => void;
  /** Map to a module, or null for Not mapped. */
  onSetModule?: (row: EngagementRequirement, moduleId: string | null) => void;
  onRemove: (row: EngagementRequirement) => void;
  onRestore: (row: EngagementRequirement) => void;
}

export function RequirementPanel({
  engagementId,
  row,
  libraryWording,
  modules = [],
  busy,
  userName,
  documentName,
  onClose,
  onCommitText,
  onSetCriticality,
  onSetClientArea,
  onSetModule,
  onRemove,
  onRestore,
}: RequirementPanelProps) {
  const removed = row.status === 'removed';
  const criticality = asCriticality(row.criticality);
  const citation = citationOf(row);
  const matchedFromClient = citation.kind === 'library' && citation.matchedFrom !== null;
  const moduleChoices = React.useMemo<ModuleOption[]>(
    () => [{ id: NOT_MAPPED_VIEW, name: NOT_MAPPED_LABEL }, ...modules],
    [modules],
  );

  return (
    <SidePanel
      aria-label={`Requirement ${row.number}`}
      identifier={row.number}
      path={`${moduleLabelOf(row)} › ${row.subcategory}`}
      onClose={onClose}
      footer={
        <>
          {removed ? (
            <WorkspaceButton disabled={busy} onClick={() => onRestore(row)}>
              Restore
            </WorkspaceButton>
          ) : (
            <WorkspaceButton variant="destructive" disabled={busy} onClick={() => onRemove(row)}>
              Remove
            </WorkspaceButton>
          )}
          <span className="text-meta text-ink-500">
            {removed ? 'Removed. Restore it to edit it.' : 'Changes save automatically'}
          </span>
        </>
      }
    >
      <PanelSection label="Requirement">
        {removed ? (
          <p className="text-body leading-[22px] text-ink-900">{row.text}</p>
        ) : (
          <PanelTextEditor
            key={`${row.id}:${row.text}`}
            initial={row.text}
            disabled={false}
            onCommit={(text) => onCommitText(row, text)}
          />
        )}
      </PanelSection>

      <PanelSection label="Criticality">
        <SegmentedControl
          aria-label="Criticality"
          options={SEGMENTS}
          value={criticality === 'not_set' ? null : criticality}
          readOnly={removed}
          onChange={(value) => onSetCriticality(row, value)}
        />
        {criticality === 'not_set' ? (
          <p className="mt-1.5 text-meta text-ink-500">Not set yet for this client.</p>
        ) : null}
      </PanelSection>

      <PanelSection label="Client area">
        {removed || !onSetClientArea ? (
          <p className="text-body-sm text-ink-900">
            {row.client_area ?? <span className="text-ink-300">None</span>}
          </p>
        ) : (
          <AutosaveField
            key={row.id}
            aria-label="Client area"
            value={row.client_area ?? ''}
            placeholder="The client’s functional area"
            maxLength={255}
            onSave={(value) => onSetClientArea(row, value.trim() || null)}
            className="h-8 text-body-sm"
          />
        )}
      </PanelSection>

      <PanelSection label="Module">
        {row.library_item_id || removed || !onSetModule ? (
          <p className="text-body-sm text-ink-900">
            {moduleLabelOf(row)}
            {row.library_item_id ? (
              <span className="block text-meta text-ink-500">The library item’s module.</span>
            ) : null}
          </p>
        ) : (
          <>
            <ModuleSelect
              aria-label="Module"
              value={row.module_id ?? NOT_MAPPED_VIEW}
              modules={moduleChoices}
              allowSkip={false}
              disabled={busy}
              onChange={(id) => onSetModule(row, id === NOT_MAPPED_VIEW ? null : id)}
            />
            {row.module_id === null ? (
              <p className="mt-1.5 text-meta text-ink-500">
                Not mapped: no vendor is rated on it until it is in a library module.
              </p>
            ) : null}
          </>
        )}
      </PanelSection>

      <PanelSection label="Citation">
        <CitationGrid items={citationItems(row, { userName, documentName })} />
        {libraryWording != null ? (
          <SuggestedWording
            current={row.text}
            library={libraryWording}
            libraryNumber={citation.kind === 'library' ? citation.libraryNumber : null}
            fromClient={matchedFromClient}
            disabled={busy}
            onUse={removed ? null : () => onCommitText(row, libraryWording)}
          />
        ) : null}
      </PanelSection>

      {row.discovery_evidence?.length ? (
        <PanelSection label="Discovery evidence">
          <DiscoveryEvidenceList evidence={row.discovery_evidence} />
        </PanelSection>
      ) : null}

      <PanelSection label="Activity">
        <RequirementActivity
          // Another requirement is another history: start clean.
          key={row.id}
          engagementId={engagementId}
          requirementId={row.id}
          revision={`${row.updated_at}|${row.status}`}
        />
      </PanelSection>
    </SidePanel>
  );
}
